/**
 * Ad auto-refresh system with viewport awareness
 * @module lib/autoRefresh
 */

const { logDebug } = require('./logger');
const { applySlotSizeFromPrebid } = require('./adSizeUtils');
const {
  applyPrebidTargetingToSlots,
  clearSlotTargeting,
  getSlotTargeting
} = require('./gptConfig');

function setupAutoRefresh(googletag, pbjs, options = {}) {
  if (typeof window === 'undefined') {
    return;
  }
  if (!googletag || typeof googletag.pubads !== 'function') {
    return;
  }

  const pubads = googletag.pubads();
  if (pubads._millaAutoRefreshInstalled) {
    return pubads._millaAutoRefreshController;
  }
  pubads._millaAutoRefreshInstalled = true;
  let destroyed = false;

  const refreshKey = options.refreshKey || 'refresh';
  const refreshValue = options.refreshValue || 'true';
  const prebidTimeout =
    typeof options.prebidTimeout === 'number' ? options.prebidTimeout : 1500;
  const shouldApplyPrebidSize = typeof options.shouldApplyPrebidSize === 'function'
    ? options.shouldApplyPrebidSize
    : () => true;
  const emptyDelayMs =
    typeof options.emptyDelayMs === 'number' ? options.emptyDelayMs : 5000;
  const emptyInitialDelayMs =
    typeof options.emptyInitialDelayMs === 'number'
      ? options.emptyInitialDelayMs
      : Math.min(emptyDelayMs, 2000);
  const hasExplicitEmptyInitialDelay = typeof options.emptyInitialDelayMs === 'number';
  const filledDelayMs =
    typeof options.filledDelayMs === 'number' ? options.filledDelayMs : 30000;
  const minRefreshIntervalMs =
    typeof options.minRefreshIntervalMs === 'number' ? options.minRefreshIntervalMs : 2000;
  const maxRefreshes =
    typeof options.maxRefreshes === 'number' ? options.maxRefreshes : 10;
  const maxEmptyRefreshes =
    typeof options.maxEmptyRefreshes === 'number' ? options.maxEmptyRefreshes : maxRefreshes;
  const maxFilledRefreshes =
    typeof options.maxFilledRefreshes === 'number' ? options.maxFilledRefreshes : maxRefreshes;
  const hasIndependentRefreshBudgets =
    typeof options.maxEmptyRefreshes === 'number'
    || typeof options.maxFilledRefreshes === 'number';
  const coordinator = options.coordinator
    && typeof options.coordinator.requestRefresh === 'function'
    ? options.coordinator
    : null;
  const staleThresholdMs =
    typeof options.staleThresholdMs === 'number' ? options.staleThresholdMs : 30000;
  const staleReentryDelayMs =
    typeof options.staleReentryDelayMs === 'number' ? options.staleReentryDelayMs : 1500;
  // Exponential backoff for persistent empty slots
  const emptyBackoffMultiplier =
    typeof options.emptyBackoffMultiplier === 'number' ? options.emptyBackoffMultiplier : 2;
  const emptyBackoffMaxDelayMs =
    typeof options.emptyBackoffMaxDelayMs === 'number' ? options.emptyBackoffMaxDelayMs : 60000;
  const emptyBackoffThreshold =
    typeof options.emptyBackoffThreshold === 'number' ? options.emptyBackoffThreshold : 3;
  // Preload margin - start observing slots before they enter viewport
  const preloadMarginPx =
    typeof options.preloadMarginPx === 'number' ? options.preloadMarginPx : 200;
  // Viewability threshold (0-1) - portion of slot that must be visible
  const viewabilityThreshold =
    typeof options.viewabilityThreshold === 'number' ? options.viewabilityThreshold : 0;
  // Minimum time in view before counting as viewable (ms)
  const viewabilityTimeMs =
    typeof options.viewabilityTimeMs === 'number' ? options.viewabilityTimeMs : 0;
  // Scroll cooldown - don't refresh if user scrolled recently
  const scrollCooldownMs =
    typeof options.scrollCooldownMs === 'number' ? options.scrollCooldownMs : 500;
  // Slot cleanup interval (ms) - how often to check for removed slots
  const cleanupIntervalMs =
    typeof options.cleanupIntervalMs === 'number' ? options.cleanupIntervalMs : 30000;
  // Network-aware: skip aggressive refresh on slow connections
  const respectSlowNetwork =
    typeof options.respectSlowNetwork === 'boolean' ? options.respectSlowNetwork : true;
  const shouldManageSlot =
    typeof options.shouldManageSlot === 'function'
      ? options.shouldManageSlot
      : () => true;

  const refreshState = {};
  const slotMap = {}; // Maps slotId to GPT slot object
  const observedElements = new Set();
  const observedTargets = {};

  // Tab visibility state
  let isTabVisible = typeof document !== 'undefined' ? !document.hidden : true;
  let pausedTimers = {}; // Stores { slotId: { remainingMs, pausedAt } }

  // Scroll tracking for cooldown
  let lastScrollAt = 0;
  let scrollHandler = null;
  let visibilityHandler = null;
  let cleanupIntervalId = null;

  // Use IntersectionObserver to track viewport visibility for all slots
  // (impressionViewable only fires for filled ads, not empty ones)
  // Maps proxy element IDs to their corresponding slot IDs (for collapsed elements)
  const proxyToSlotMap = {};

  // Viewability tracking - slots waiting for time-in-view requirement
  const viewabilityTimers = {};

  const viewportObserver = typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(
        function (entries) {
          if (destroyed) return;
          entries.forEach(function (entry) {
            const targetId = entry.target.id;
            // Check if this is a proxy element observing on behalf of a collapsed slot
            const slotId = proxyToSlotMap[targetId] || targetId;
            const slot = slotMap[slotId];
            if (!slot) {
              return;
            }

            const state = refreshState[slotId] || {};
            const wasInView = state.inView;
            const isNowInView = entry.isIntersecting;

            // Handle viewability time requirement
            if (viewabilityTimeMs > 0 && isNowInView && !wasInView) {
              // Start viewability timer
              clearViewabilityTimer(slotId);
              viewabilityTimers[slotId] = window.setTimeout(function () {
                state.inView = true;
                state.viewabilityMet = true;
                refreshState[slotId] = state;
                handleSlotEnteredView(slot, state);
              }, viewabilityTimeMs);
              // Don't mark as in view yet
              refreshState[slotId] = state;
              return;
            } else if (!isNowInView && viewabilityTimers[slotId]) {
              // Left viewport before viewability time met
              clearViewabilityTimer(slotId);
            }

            state.inView = isNowInView;
            refreshState[slotId] = state;

            if (isNowInView && !wasInView) {
              handleSlotEnteredView(slot, state);
            } else if (!isNowInView && wasInView) {
              // Slot left viewport - clear any pending refresh
              clearSlotTimer(slotId);
              clearViewabilityTimer(slotId);
            }
          });
        },
        {
          threshold: viewabilityThreshold,
          rootMargin: `${preloadMarginPx}px`
        }
      )
    : null;

  function clearViewabilityTimer(slotId) {
    if (viewabilityTimers[slotId]) {
      clearTimeout(viewabilityTimers[slotId]);
      delete viewabilityTimers[slotId];
    }
  }

  function handleSlotEnteredView(slot, state) {
    const slotId = slot.getSlotElementId && slot.getSlotElementId();
    if (!slotId) return;

    logDebug('refresh:enter', {
      slotId,
      hasFill: state.hasFill,
      refreshCount: state.refreshCount || 0
    });

    // Slot just entered viewport - refresh if empty, schedule if filled
    if (typeof state.hasFill === 'boolean') {
      if (state.hasFill === false) {
        const consecutiveEmpty = state.consecutiveEmpty || 0;
        const delay = calculateEmptyDelay(consecutiveEmpty);
        scheduleSlotRefresh(slot, delay);
      } else {
        // Check if slot is stale (last render was long ago)
        const lastRenderAt = state.lastRenderAt || 0;
        const timeSinceRender = lastRenderAt > 0 ? Date.now() - lastRenderAt : 0;
        if (timeSinceRender > staleThresholdMs) {
          logDebug('refresh:stale-reentry', { slotId, timeSinceRender });
          // Stale slot re-entered viewport - refresh quickly
          scheduleSlotRefresh(slot, staleReentryDelayMs);
        } else {
          scheduleSlotRefresh(slot, filledDelayMs);
        }
      }
    }
  }

  // Calculate delay with exponential backoff for persistent empty slots
  function calculateEmptyDelay(consecutiveEmpty) {
    if (consecutiveEmpty === 0 || (hasExplicitEmptyInitialDelay && consecutiveEmpty === 1)) {
      return emptyInitialDelayMs;
    }
    if (consecutiveEmpty < emptyBackoffThreshold) {
      return emptyDelayMs;
    }
    // Apply exponential backoff after threshold
    const backoffFactor = Math.pow(emptyBackoffMultiplier, consecutiveEmpty - emptyBackoffThreshold);
    const delay = Math.min(emptyDelayMs * backoffFactor, emptyBackoffMaxDelayMs);
    return Math.round(delay);
  }

  function clearSlotTimer(slotId) {
    const state = refreshState[slotId];
    if (state && state.timerId) {
      clearTimeout(state.timerId);
      state.timerId = null;
    }
  }

  // Check if network is slow (2g, slow-3g)
  function isSlowNetwork() {
    if (!respectSlowNetwork) return false;
    try {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn && conn.effectiveType) {
        return conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g';
      }
    } catch (e) {
      // Network API not available
    }
    return false;
  }

  // Check if user recently scrolled (within cooldown period)
  function isInScrollCooldown() {
    if (scrollCooldownMs <= 0) return false;
    return Date.now() - lastScrollAt < scrollCooldownMs;
  }

  // Set up scroll tracking
  if (typeof window !== 'undefined' && scrollCooldownMs > 0) {
    let scrollThrottled = false;
    scrollHandler = function () {
      if (scrollThrottled) return;
      scrollThrottled = true;
      lastScrollAt = Date.now();
      setTimeout(function () {
        scrollThrottled = false;
      }, 100);
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });
  }

  // Tab visibility handling - pause timers when tab is hidden
  function pauseAllTimers() {
    const now = Date.now();
    Object.keys(refreshState).forEach(function (slotId) {
      const state = refreshState[slotId];
      if (state && state.timerId && state.timerScheduledAt) {
        const elapsed = now - state.timerScheduledAt;
        const remaining = Math.max(0, (state.timerDelayMs || 0) - elapsed);
        clearTimeout(state.timerId);
        state.timerId = null;
        pausedTimers[slotId] = { remainingMs: remaining, slot: slotMap[slotId] };
      }
    });
  }

  function resumeAllTimers() {
    Object.keys(pausedTimers).forEach(function (slotId) {
      const paused = pausedTimers[slotId];
      if (paused && paused.slot && paused.remainingMs >= 0) {
        scheduleSlotRefresh(paused.slot, paused.remainingMs);
      }
    });
    pausedTimers = {};
  }

  if (typeof document !== 'undefined') {
    visibilityHandler = function () {
      if (document.hidden) {
        isTabVisible = false;
        pauseAllTimers();
      } else {
        isTabVisible = true;
        resumeAllTimers();
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
  }

  // Cleanup: periodically remove state for slots no longer in DOM
  function removeManagedSlot(slotId) {
    const normalizedSlotId = String(slotId || '').trim();
    if (!normalizedSlotId) return;
    clearSlotTimer(normalizedSlotId);
    clearViewabilityTimer(normalizedSlotId);
    const observedTarget = observedTargets[normalizedSlotId];
    if (viewportObserver && observedTarget) {
      try {
        viewportObserver.unobserve(observedTarget);
      } catch (e) {
        // The observed DOM node may already be detached.
      }
    }
    delete observedTargets[normalizedSlotId];
    delete refreshState[normalizedSlotId];
    delete slotMap[normalizedSlotId];
    observedElements.delete(normalizedSlotId);
    delete pausedTimers[normalizedSlotId];
    if (coordinator && typeof coordinator.destroy === 'function') {
      coordinator.destroy(normalizedSlotId);
    }
    Object.keys(proxyToSlotMap).forEach(function (proxyId) {
      if (proxyToSlotMap[proxyId] === normalizedSlotId) {
        delete proxyToSlotMap[proxyId];
      }
    });
  }

  function cleanupRemovedSlots() {
    Object.keys(slotMap).forEach(function (slotId) {
      if (!document.getElementById(slotId)) {
        removeManagedSlot(slotId);
      }
    });
  }

  // Start cleanup interval
  if (typeof window !== 'undefined' && cleanupIntervalMs > 0) {
    cleanupIntervalId = setInterval(cleanupRemovedSlots, cleanupIntervalMs);
  }

  function isElementInViewport(element) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    // When GPT hides empty slots, getBoundingClientRect returns all zeros.
    // These elements are NOT in viewport - they're hidden/collapsed.
    if (rect.top === 0 && rect.bottom === 0 && rect.left === 0 && rect.right === 0) {
      return false;
    }

    // For empty slots with 0 height that are NOT hidden (have a position),
    // check if position is within viewport
    if (rect.height === 0 || rect.width === 0) {
      return rect.top >= 0 && rect.top <= viewportHeight && rect.left >= 0 && rect.left <= viewportWidth;
    }

    // Check if any part of the element is visible
    return rect.top < viewportHeight && rect.bottom > 0 && rect.left < viewportWidth && rect.right > 0;
  }

  function findVisibleParent(element) {
    // Find a visible parent container to observe when the slot itself is collapsed
    // Look for common ad wrapper classes or just walk up the DOM
    let parent = element.parentElement;
    while (parent && parent !== document.body) {
      const rect = parent.getBoundingClientRect();
      // Check if parent has layout (not all zeros)
      if (!(rect.top === 0 && rect.bottom === 0 && rect.left === 0 && rect.right === 0)) {
        // Parent has layout, check if it has reasonable dimensions
        if (rect.height > 0 || rect.width > 0) {
          return parent;
        }
      }
      parent = parent.parentElement;
    }
    return null;
  }

  function observeSlot(slot, isEmpty) {
    const slotId = slot.getSlotElementId && slot.getSlotElementId();
    if (!slotId) return;

    const element = document.getElementById(slotId);
    if (!element) return;

    // Set up state BEFORE observing (IntersectionObserver callback needs hasFill to be set)
    const inViewport = isElementInViewport(element);
    const state = refreshState[slotId] || {};
    state.emptyRefreshCount = state.emptyRefreshCount || 0;
    state.filledRefreshCount = state.filledRefreshCount || 0;
    state.inView = inViewport;
    state.hasFill = !isEmpty;
    refreshState[slotId] = state;
    slotMap[slotId] = slot;

    // Start observing if not already (for future viewport changes)
    if (viewportObserver && !observedElements.has(slotId)) {
      // For collapsed/hidden elements (inViewport false due to all-zeros rect),
      // observe a visible parent container instead
      let elementToObserve = element;
      if (isEmpty && !inViewport) {
        const visibleParent = findVisibleParent(element);
        if (visibleParent && visibleParent.id) {
          elementToObserve = visibleParent;
          proxyToSlotMap[visibleParent.id] = slotId;
        } else if (visibleParent) {
          // Parent doesn't have an ID, create a temporary one
          const proxyId = `milla-proxy-${slotId}`;
          visibleParent.id = visibleParent.id || proxyId;
          elementToObserve = visibleParent;
          proxyToSlotMap[visibleParent.id] = slotId;
        }
      }

      viewportObserver.observe(elementToObserve);
      observedElements.add(slotId);
      observedTargets[slotId] = elementToObserve;
    }

    // If empty and in viewport, handle refresh with backoff
    if (inViewport && isEmpty) {
      const consecutiveEmpty = state.consecutiveEmpty || 0;
      const delay = calculateEmptyDelay(consecutiveEmpty);
      scheduleSlotRefresh(slot, delay);
    }
  }

  function scheduleSlotRefresh(slot, delayMs) {
    const slotId = slot.getSlotElementId && slot.getSlotElementId();
    if (!slotId) return;
    clearSlotTimer(slotId);
    const state = refreshState[slotId] || { refreshCount: 0 };

    const refreshLimit = state.hasFill === false ? maxEmptyRefreshes : maxFilledRefreshes;
    const refreshCount = state.hasFill === false
      ? (state.emptyRefreshCount || 0)
      : (state.filledRefreshCount || 0);
    // Empty and filled inventory have independent refresh budgets.
    if (
      refreshCount >= refreshLimit
      || (!hasIndependentRefreshBudgets && state.refreshCount >= maxRefreshes)
    ) {
      return;
    }

    // Don't schedule if not in viewport
    if (!state.inView) {
      return;
    }

    // Don't schedule if tab is not visible
    if (!isTabVisible) {
      return;
    }

    // Skip aggressive refresh on slow networks (only for filled slots)
    if (state.hasFill && isSlowNetwork()) {
      return;
    }

    // Track timer info for pause/resume functionality
    state.timerScheduledAt = Date.now();
    state.timerDelayMs = delayMs;

    logDebug('refresh:schedule', { slotId, delayMs });

    state.timerId = window.setTimeout(function () {
      if (destroyed) return;
      // Double-check viewport status before refreshing
      const currentState = refreshState[slotId];
      if (!currentState || !currentState.inView) {
        return;
      }

      // Check scroll cooldown - if user is actively scrolling, delay the refresh
      if (isInScrollCooldown()) {
        scheduleSlotRefresh(slot, scrollCooldownMs);
        return;
      }

      if (coordinator && typeof coordinator.isInFlight === 'function' && coordinator.isInFlight(slotId)) {
        scheduleSlotRefresh(slot, minRefreshIntervalMs);
        return;
      }

      refreshSlot(slot);
    }, delayMs);
    refreshState[slotId] = state;
  }

  function refreshSlot(slot) {
    const slotId = slot.getSlotElementId && slot.getSlotElementId();
    if (!slotId) return;
    const state = refreshState[slotId] || {};
    const lastActionAt = Math.max(state.lastRenderAt || 0, state.lastRefreshAt || 0);
    if (lastActionAt && Date.now() - lastActionAt < minRefreshIntervalMs) {
      scheduleSlotRefresh(slot, minRefreshIntervalMs);
      return;
    }
    state.refreshCount = (state.refreshCount || 0) + 1;
    if (state.hasFill === false) {
      state.emptyRefreshCount = (state.emptyRefreshCount || 0) + 1;
    } else {
      state.filledRefreshCount = (state.filledRefreshCount || 0) + 1;
    }
    state.lastRefreshAt = Date.now();
    refreshState[slotId] = state;
    if (coordinator) {
      logDebug('refresh:coordinated', { slotId });
      coordinator.requestRefresh(slotId, {
        reason: state.hasFill === false ? 'empty_refresh' : 'filled_refresh',
        refreshCount: state.refreshCount || 0
      });
      return;
    }
    clearSlotTargeting(slot);
    if (!pbjs || typeof pbjs.requestBids !== 'function') {
      logDebug('refresh:direct', { slotId });
      googletag.pubads().refresh([slot]);
      return;
    }
    logDebug('refresh:prebid', { slotId, prebidTimeout });
    pbjs.requestBids({
      timeout: prebidTimeout,
      adUnitCodes: [slotId],
      bidsBackHandler: function () {
        logDebug('refresh:prebid:done', { slotId });
        if (shouldApplyPrebidSize(slotId)) {
          applySlotSizeFromPrebid(slotId, pbjs);
        }
        applyPrebidTargetingToSlots(pbjs, slot);
        googletag.pubads().refresh([slot]);
      }
    });
  }

  function handleImpressionViewable(event) {
    if (destroyed) return;
    const slot = event.slot;
    const slotId = slot.getSlotElementId && slot.getSlotElementId();
    if (!slotId) return;
    if (!shouldManageSlot(slotId, slot)) return;
    if (getSlotTargeting(slot, refreshKey).indexOf(refreshValue) === -1) return;

    const state = refreshState[slotId] || {};
    // impressionViewable confirms the slot is in view (for filled ads)
    state.inView = true;
    refreshState[slotId] = state;

    if (state.hasFill === false) {
      const consecutiveEmpty = state.consecutiveEmpty || 0;
      const delay = calculateEmptyDelay(consecutiveEmpty);
      scheduleSlotRefresh(slot, delay);
    } else {
      scheduleSlotRefresh(slot, filledDelayMs);
    }
  }

  function handleSlotRenderEnded(event) {
    if (destroyed) return;
    const slot = event.slot;
    const slotId = slot.getSlotElementId && slot.getSlotElementId();
    if (!slotId) return;
    if (!shouldManageSlot(slotId, slot)) return;
    if (getSlotTargeting(slot, refreshKey).indexOf(refreshValue) === -1) return;

    const state = refreshState[slotId] || {};
    state.lastRenderAt = Date.now();

    const isEmpty = !!event.isEmpty;

    // Track consecutive empty renders for backoff
    if (isEmpty) {
      state.consecutiveEmpty = (state.consecutiveEmpty || 0) + 1;
    } else {
      // Reset consecutive empty count when slot fills
      state.consecutiveEmpty = 0;
    }

    refreshState[slotId] = state;

    // Start observing and immediately check viewport + schedule refresh if empty
    observeSlot(slot, isEmpty);
  }

  pubads.addEventListener('impressionViewable', handleImpressionViewable);
  pubads.addEventListener('slotRenderEnded', handleSlotRenderEnded);

  const controller = {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      Object.keys(refreshState).forEach((slotId) => {
        clearSlotTimer(slotId);
        clearViewabilityTimer(slotId);
      });
      if (cleanupIntervalId) clearInterval(cleanupIntervalId);
      if (viewportObserver && typeof viewportObserver.disconnect === 'function') {
        viewportObserver.disconnect();
      }
      if (scrollHandler && typeof window !== 'undefined') {
        window.removeEventListener('scroll', scrollHandler);
      }
      if (visibilityHandler && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', visibilityHandler);
      }
      if (typeof pubads.removeEventListener === 'function') {
        pubads.removeEventListener('impressionViewable', handleImpressionViewable);
        pubads.removeEventListener('slotRenderEnded', handleSlotRenderEnded);
      }
      pubads._millaAutoRefreshInstalled = false;
      if (pubads._millaAutoRefreshController === controller) {
        delete pubads._millaAutoRefreshController;
      }
    },
    getSlotState(slotId) {
      const state = refreshState[slotId];
      return state ? { ...state, timerId: undefined } : null;
    },
    removeSlot(slotId) {
      removeManagedSlot(slotId);
    }
  };
  pubads._millaAutoRefreshController = controller;
  return controller;
}

module.exports = { setupAutoRefresh };
